import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { LogOut, User } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { ProfileAvatar } from "@/shared/ui/ProfileAvatar";
import {
  clearSession,
  getAccountDisplayLabel,
  getRoleDisplayLabel,
  getStoredAccountType,
  AccountType,
} from "@/modules/safecheck/auth";
import {
  enterpriseBrand,
  getAccountAvatarInitials,
  hospitalBrand,
} from "@/modules/safecheck/branding";
import { cn } from "@/shared/lib/cn";

export function SidebarProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [accountType, setAccountType] = useState<AccountType | null>(() => {
    if (typeof window === "undefined") return null;
    return getStoredAccountType();
  });
  const [userName, setUserName] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return getAccountDisplayLabel();
  });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAccountType(getStoredAccountType());
    setUserName(getAccountDisplayLabel());
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const accountLabel =
    userName ??
    (accountType === "hospital"
      ? hospitalBrand.alt.toUpperCase()
      : accountType === "enterprise"
        ? enterpriseBrand.alt.toUpperCase()
        : accountType === "admin"
          ? t.header.userNames.admin
          : accountType === "auditor"
            ? t.header.userNames.auditor
            : accountType
              ? t.safecheck.accountType.types[accountType].title
              : t.header.profile);
  const roleLabel = getRoleDisplayLabel();
  const isOrgAccount = accountType === "hospital" || accountType === "enterprise";

  const handleOpenProfile = () => {
    setIsOpen(false);
    navigate("/dashboard/settings/user");
  };

  const handleLogout = () => {
    clearSession();
    setIsOpen(false);
    navigate("/login");
  };

  return (
    <div ref={menuRef} className="relative mb-8 border-b border-sidebar-border pb-6">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full flex-col items-center gap-3 text-center"
      >
        <div
          className={cn(
            "relative overflow-hidden rounded-full ring-2 ring-sidebar-border",
            isOpen && "ring-accent",
          )}
        >
          <ProfileAvatar
            initials={getAccountAvatarInitials(accountType, accountLabel)}
            size={64}
            variant="sidebar"
          />
        </div>
        <div>
          <p className="text-sm font-semibold text-sidebar-fg">{accountLabel}</p>
          <p
            className={cn(
              "max-w-[200px] leading-snug text-sidebar-muted",
              isOrgAccount
                ? "font-roboto text-sm font-medium tracking-normal"
                : "text-[10px] font-bold tracking-[0.14em] uppercase",
            )}
          >
            {roleLabel}
          </p>
        </div>
      </button>

      {isOpen && (
        <div className="theme-card absolute left-0 right-0 top-full z-50 mt-3 overflow-hidden p-1.5">
          <button
            type="button"
            onClick={handleOpenProfile}
            className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-card-muted"
          >
            <User size={16} className="text-primary" />
            {t.header.profile}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-card-muted"
          >
            <LogOut size={16} />
            {t.header.logout}
          </button>
        </div>
      )}
    </div>
  );
}
